import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/MidwayAuthContext';
import type { PortalContent } from './content';

const ContentContext = createContext<PortalContent | undefined>(undefined);

type LoadState = 'loading' | 'ready' | 'redirecting' | 'error';

/**
 * Fetches the sensitive portal content from the Midway-protected
 * `/api/content` endpoint at runtime so it never lives in the static bundle.
 *
 * Children are not rendered until the content has loaded successfully, so every
 * consumer of `useContent()` is guaranteed non-null data. On 401/403 (or a
 * network error that indicates an expired/missing session) the user is
 * redirected to Midway sign-in.
 */
export function ContentProvider({ children }: { children: ReactNode }) {
  const { signIn } = useAuth();
  const [content, setContent] = useState<PortalContent | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    let cancelled = false;

    async function loadContent() {
      try {
        const response = await fetch('/api/content', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });

        // Session missing or expired -> bounce to Midway sign-in.
        if (response.status === 401 || response.status === 403) {
          if (!cancelled) {
            setState('redirecting');
            signIn();
          }
          return;
        }

        if (!response.ok) {
          if (!cancelled) {
            setState('error');
          }
          return;
        }

        const data = (await response.json()) as PortalContent;
        if (!cancelled) {
          setContent(data);
          setState('ready');
        }
      } catch {
        // A network error here most often means the request was blocked by the
        // Midway edge before reaching the API -> treat as unauthenticated.
        if (!cancelled) {
          setState('redirecting');
          signIn();
        }
      }
    }

    void loadContent();
    return () => {
      cancelled = true;
    };
  }, [signIn]);

  if (state === 'ready' && content) {
    return (
      <ContentContext.Provider value={content}>
        {children}
      </ContentContext.Provider>
    );
  }

  return (
    <div className="auth-gate">
      <div className="auth-gate__card">
        <div className="auth-gate__spinner" aria-hidden />
        <p className="auth-gate__text">
          {state === 'redirecting'
            ? 'Redirecting to Midway sign-in...'
            : state === 'error'
              ? 'Unable to load portal content. Please refresh to try again.'
              : 'Loading portal content...'}
        </p>
      </div>
    </div>
  );
}

/** Returns the loaded {@link PortalContent}. Throws if used outside the provider. */
export function useContent(): PortalContent {
  const ctx = useContext(ContentContext);
  if (!ctx) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return ctx;
}
